import { fetchServer } from "@/features/utils/fetch";
import type {
  AccountReason,
  CheckIbanBic,
  CheckValidationIban,
  CreateQuizResponse,
  PaymentUsage,
  QuizResponses,
  SourceIncome,
  SourceIncomeResponse
} from "@/features/user/quiz/types";

export class QuizAPIClient {
  static async getSourcesIncome(locale: string): Promise<SourceIncomeResponse> {
    const response = await fetchServer(`public/quiz_source_income/${locale}`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) throw new Error("Error");

    const res = await response.json();
    return res;
  }

  static async getCategories() {
    const response = await fetchServer(`form_level/bronze/get_categories`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) throw new Error("Error");

    const res = await response.json();

    return res;
  }

  static async getOriginAssets(): Promise<SourceIncome[]> {
    return [
      {
        Code: 1,
        Nom: "Création d’entreprise",
        NomEn: "Setting up a company",
        uuid: "154151541",
        id: 1,
        defaultName: "Création d’entreprise"
      },
      {
        Code: 2,
        Nom: "Revenus immobiliers",
        NomEn: "Real estate income",
        uuid: "15415154frf1",
        id: 2,
        defaultName: "Revenus immobiliers"
      },
      {
        Code: 3,
        Nom: "Divorce",
        NomEn: "Divorce",
        uuid: "354353543",
        id: 3,
        defaultName: "Divorce"
      },
      {
        Code: 4,
        Nom: "Dons",
        NomEn: "Donations",
        uuid: "154151541",
        id: 4,
        defaultName: "Dons"
      },
      {
        Code: 5,
        Nom: "Cryptos",
        NomEn: "Cryptos",
        uuid: "155151551",
        id: 5,
        defaultName: "Cryptos"
      },

      {
        Code: 6,
        Nom: "Vente d’entreprise",
        NomEn: "Sale of a company",
        uuid: "155151551",
        id: 6,
        defaultName: "Vente d’entreprise"
      },
      {
        Code: 7,
        Nom: "Autres",
        NomEn: "Others",
        uuid: "155151551",
        id: 7,
        defaultName: "Autres"
      },
      {
        Code: 8,
        Nom: "Héritage",
        NomEn: "Heritage",
        uuid: "155151551",
        id: 8,
        defaultName: "Héritage"
      },
      {
        Code: 9,
        Nom: "Investissements / Revenus d’assurance vie",
        NomEn: "Investments / Life insurance income",
        uuid: "155151551",
        id: 9,
        defaultName: "Investissements / Revenus d’assurance vie"
      }
    ];
  }

  static async getMovableIncome(): Promise<SourceIncome[]> {
    return [
      {
        Code: 1,
        Nom: "Compte bancaire, épargne, espèces",
        NomEn: "Bank account, savings, cash",
        uuid: "154151541",
        id: 1,
        defaultName: "Compte bancaire, épargne, espèces"
      },
      {
        Code: 2,
        Nom: "Titres non côtés",
        NomEn: "Unlisted securities",
        uuid: "15415154frf1",
        id: 2,
        defaultName: "Titres non côtés"
      },
      {
        Code: 3,
        Nom: "Automobiles, bateaux, avions",
        NomEn: "Cars, boats, planes",
        uuid: "354353543",
        id: 3,
        defaultName: "Automobiles, bateaux, avions"
      },
      {
        Code: 4,
        Nom: "Titres côtés",
        NomEn: "Quoted securities",
        uuid: "154151541",
        id: 4,
        defaultName: "Titres côtés"
      },
      {
        Code: 5,
        Nom: "Cryptos",
        NomEn: "Cryptos",
        uuid: "155151551",
        id: 5,
        defaultName: "Cryptos"
      },

      {
        Code: 6,
        Nom: "Objets d’art",
        NomEn: "Art objects",
        uuid: "155151551",
        id: 6,
        defaultName: "Objets d’art"
      },
      {
        Code: 7,
        Nom: "Autres",
        NomEn: "Others",
        uuid: "155151551",
        id: 7,
        defaultName: "Autres"
      }
    ];
  }

  static async getAccountReasons(locale: string): Promise<AccountReason[]> {
    const response = await fetchServer(`public/account_reason/${locale}`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new Error("Error");

    const res = await response.json();
    return res;
  }

  static async getPaymentsUsage(locale: string): Promise<PaymentUsage[]> {
    const response = await fetchServer(`public/payment_usage/${locale}`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new Error("Error");

    const res = await response.json();
    return res;
  }
  static async create(quizResponses: QuizResponses): Promise<CreateQuizResponse> {
    const response = await fetchServer("/user/account_quiz/create", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: quizResponses
    });

    if (!response.ok) throw new Error();
    const json: { success: boolean } = await response.json();

    return json;
  }

  static async checkIban(iban: string): Promise<CheckIbanBic> {
    const response = await fetchServer("/user/account_quiz/check_iban", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: { iban }
    });

    if (!response.ok) throw new Error();
    const json: { success: boolean; bic?: string } = await response.json();

    return json;
  }
  static async isValidatedIban(uuidIban: string, leUuid?: string): Promise<CheckValidationIban> {
    const response = await fetchServer(
      leUuid
        ? `/aliasIban/validation/${uuidIban}?contextLegalEntityUuid=${leUuid}`
        : `/aliasIban/validation/${uuidIban}`,
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );

    if (!response.ok) throw new Error();
    const json = await response.json();

    return json;
  }

  static async validateIban(uuidIban: string, leUuid?: string): Promise<CheckValidationIban> {
    const response = await fetchServer(
      leUuid
        ? `/aliasIban/request-validation?contextLegalEntityUuid=${leUuid}`
        : "/aliasIban/request-validation",
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: { uuidIban }
      }
    );

    if (!response.ok) throw new Error();
    const json = await response.json();

    return json;
  }

  static async verifyIban(iban: string, leUuid?: string): Promise<CheckIbanBic> {
    const response = await fetchServer(
      leUuid
        ? `/user/account_quiz/iban/verify?contextLegalEntityUuid=${leUuid}`
        : "/user/account_quiz/iban/verify",
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: { iban }
      }
    );

    if (!response.ok) throw new Error();
    const json = await response.json();

    return json;
  }
}
